<template>
  <div class="main-content-container overflow-hidden">
    <PageTitle pageTitle="Users" subTitle="Management" />

    <div class="card bg-white border-0 rounded-3 mb-4">
      <div class="card-body p-0">
        <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 p-4">
          <form class="position-relative table-src-form me-0" @submit.prevent>
            <input type="text" class="form-control" placeholder="Search users..." v-model="searchQuery" />
            <i class="material-symbols-outlined position-absolute top-50 start-0 translate-middle-y">search</i>
          </form>
          <RouterLink to="/users/create" class="btn btn-outline-primary py-1 px-2 px-sm-4 fs-14 fw-medium rounded-3 hover-bg">
            <span class="py-sm-1 d-block">
              <i class="ri-add-line d-none d-sm-inline-block me-1"></i>
              <span>Add New User</span>
            </span>
          </RouterLink>
        </div>

        <div class="default-table-area style-two default-table-width">
          <div class="table-responsive">
            <table class="table align-middle">
              <thead>
                <tr>
                  <th scope="col">ID</th>
                  <th scope="col">Name</th>
                  <th scope="col">Email</th>
                  <th scope="col">Role</th>
                  <th scope="col">Created At</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="loading">
                  <td colspan="6" class="text-center py-4">Loading...</td>
                </tr>
                <tr v-else-if="filteredUsers.length === 0">
                  <td colspan="6" class="text-center py-4">No users found.</td>
                </tr>
                <tr v-else v-for="user in filteredUsers" :key="user.id">
                  <td>#{{ user.id }}</td>
                  <td>
                    <h6 class="mb-0 fs-14 fw-medium">{{ user.name }}</h6>
                  </td>
                  <td class="text-body">{{ user.email }}</td>
                  <td>
                    <span class="badge bg-opacity-10 p-2 fs-12 fw-normal text-capitalize" :class="user.role === 'super_admin' ? 'bg-primary text-primary' : 'bg-success text-success'">
                      {{ user.role === 'super_admin' ? 'Super Admin' : 'Admin' }}
                    </span>
                  </td>
                  <td class="text-body">{{ new Date(user.created_at).toLocaleDateString() }}</td>
                  <td>
                    <div class="d-flex align-items-center gap-1">
                      <RouterLink :to="`/users/${user.id}/edit`" class="ps-0 border-0 bg-transparent lh-1 position-relative top-2">
                        <i class="material-symbols-outlined fs-16 text-body">edit</i>
                      </RouterLink>
                      <button @click="deleteUser(user.id)" class="ps-0 border-0 bg-transparent lh-1 position-relative top-2">
                        <i class="material-symbols-outlined fs-16 text-danger">delete</i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from 'vue';
import PageTitle from '@/components/Common/PageTitle.vue';
import userService from '@/services/userService';

export default defineComponent({
  name: 'UserListPage',
  components: {
    PageTitle
  },
  setup() {
    const users = ref<any[]>([]);
    const loading = ref(true);
    const searchQuery = ref('');

    const fetchUsers = async () => {
      loading.value = true;
      try {
        const response = await userService.getAll();
        users.value = response.data;
      } catch (error) {
        console.error('Error fetching users:', error);
      } finally {
        loading.value = false;
      }
    };

    const deleteUser = async (id: number) => {
      // Prevent deleting self
      const currentUserStr = localStorage.getItem('user');
      if (currentUserStr) {
        try {
          const currentUser = JSON.parse(currentUserStr);
          if (currentUser.id === id) {
            alert('You cannot delete your own account.');
            return;
          }
        } catch (e) {
          console.error(e);
        }
      }

      if (confirm('Are you sure you want to delete this user?')) {
        try {
          await userService.delete(id);
          fetchUsers();
        } catch (error: any) {
          console.error('Error deleting user:', error);
          if (error.response?.data?.message) {
            alert(error.response.data.message);
          } else {
            alert('Failed to delete user.');
          }
        }
      }
    };

    const filteredUsers = computed(() => {
      if (!searchQuery.value) return users.value;
      const q = searchQuery.value.toLowerCase();
      return users.value.filter(u => 
        u.name.toLowerCase().includes(q) || 
        u.email.toLowerCase().includes(q) ||
        u.role.toLowerCase().includes(q)
      );
    });

    onMounted(() => {
      fetchUsers();
    });

    return {
      users,
      loading,
      searchQuery,
      filteredUsers,
      deleteUser
    };
  }
});
</script>
